import { defineComponent as _defineComponent } from 'vue';
import { faAngleDown, faAngleUp, faExclamationCircle, faLink, faTimes, faUnlink, } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton, BButtonGroup, BCard, BCardBody, BCardHeader } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import draggable from "vuedraggable";
import { useConfirmDialog } from "@/composables/confirmDialog";
import { Toast } from "@/composables/toast";
import STATES from "@/mvc/dataset/states";
import { useDatatypesMapperStore } from "@/stores/datatypesMapperStore";
// import levenshteinDistance from '@/utils/levenshtein';
import localize from "@/utils/localization";
import { naturalSort } from "@/utils/naturalSort";
import Heading from "../Common/Heading.vue";
import PairedElementView from "./PairedElementView.vue";
import UnpairedDatasetElementView from "./UnpairedDatasetElementView.vue";
import CollectionCreator from "@/components/Collections/common/CollectionCreator.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'PairedListCollectionCreator',
    props: {
        historyId: null,
        initialElements: null,
        defaultHideSourceItems: { type: Boolean },
        fromSelection: { type: Boolean },
        extensions: null
    },
    emits: ["clicked-create", "on-cancel"],
    setup(__props, { emit }) {
        const props = __props;
        const NOT_VALID_ELEMENT_MSG = localize("is not a valid element for this collection");
        const COMMON_FILTERS = {
            illumina: ["_1", "_2"],
            Rs: ["_R1", "_R2"],
            dot12s: [".1.fastq", ".2.fastq"],
        };
        const DEFAULT_FILTER = "illumina";
        const MATCH_PERCENTAGE = 0.99;
        // Titles and help text
        const CHOOSE_FILTER_TITLE = localize("Choose from common filters");
        const FILTER_TEXT_PLACEHOLDER = localize("Filter text");
        const FILTER_TEXT_TITLE = localize("Use this box to filter elements, using simple matching or regular expressions.");
        const ERROR_TEXT = localize("There was a problem creating the collection.");
        const INVALID_HEADER = localize("The following selections could not be included due to problems:");
        const ALL_INVALID_ELEMENTS_PART_ONE = localize("At least two elements are needed for the collection. You may need to");
        const CANCEL_TEXT = localize("Cancel");
        // Titles and help text computed
        const noElementsHeader = props.fromSelection ? localize("No elements selected") : localize("No elements available");
        const allInvalidElementsPartTwo = props.fromSelection
            ? localize("and reselect new elements.")
            : localize("and change your current history or upload valid datasets for this collection.");
        // Flags
        const state = ref("build");
        const hideSourceItems = ref(props.defaultHideSourceItems || false);
        const autoPairsPossible = ref(true);
        const twoPassAutoPairing = ref(true);
        const removeExtensions = ref(true);
        const firstAutoPairDone = ref(false);
        const showPairingSection = ref(true);
        // Elements
        const workingElements = ref([]);
        const invalidElements = ref([]);
        const generatedPairs = ref([]);
        const selectedForwardElement = ref(null);
        const selectedReverseElement = ref(null);
        const atLeastOnePair = ref(true);
        // Filters
        const forwardFilter = ref(COMMON_FILTERS[DEFAULT_FILTER][0] || "");
        const reverseFilter = ref(COMMON_FILTERS[DEFAULT_FILTER][1] || "");
        const hasFilter = computed(() => forwardFilter.value || reverseFilter.value);
        // Autopairing
        const strategy = ref(autoPairLCS);
        const duplicatePairNames = ref([]);
        const canClearFilters = computed(() => {
            return forwardFilter.value || reverseFilter.value;
        });
        const canAutoPair = computed(() => {
            return forwardFilter.value && reverseFilter.value && pairableElements.value.length > 0;
        });
        const forwardElements = computed(() => {
            return filterElements(workingElements.value, forwardFilter.value);
        });
        const reverseElements = computed(() => {
            return filterElements(workingElements.value, reverseFilter.value);
        });
        const pairableElements = computed(() => {
            const pairable = [];
            forwardElements.value.forEach((forwardElement, index) => {
                const reverseElement = reverseElements.value[index];
                if (!!reverseElement && !!forwardElement && forwardElement.id != reverseElement.id) {
                    pairable.push({
                        forward: forwardElement,
                        reverse: reverseElement,
                        name: _guessNameForPair(forwardElement, reverseElement),
                    });
                }
            });
            return pairable;
        });
        const autoPairButton = computed(() => {
            let variant;
            let icon;
            let text;
            if (!canAutoPair.value) {
                variant = "secondary";
                icon = faLink;
                text = localize("Specify simple filters to divide datasets into forward and reverse reads for pairing.");
            }
            else if (!firstAutoPairDone.value && pairableElements.value.length > 0) {
                variant = "primary";
                icon = faExclamationCircle;
                text = localize("Click to auto-pair datasets based on the current filters");
            }
            else if (pairableElements.value.length > 0) {
                variant = "secondary";
                icon = faLink;
                text = localize("Auto-pair possible based on current filters");
            }
            else {
                variant = "secondary";
                icon = faLink;
                text = localize("Click to attempt auto-pairing datasets");
            }
            return { variant, icon, text };
        });
        const numOfUnpairedForwardElements = computed(() => {
            return forwardElements.value.length;
        });
        const numOfFilteredOutForwardElements = computed(() => {
            return workingElements.value.length - numOfUnpairedForwardElements.value;
        });
        const numOfUnpairedReverseElements = computed(() => {
            return reverseElements.value.length;
        });
        const numOfFilteredOutReverseElements = computed(() => {
            return workingElements.value.length - numOfUnpairedReverseElements.value;
        });
        const numOfPairs = computed(() => {
            return generatedPairs.value.length;
        });
        const returnInvalidElementsLength = computed(() => {
            return invalidElements.value.length > 0;
        });
        const returnInvalidElements = computed(() => {
            return invalidElements.value;
        });
        const allElementsAreInvalid = computed(() => {
            return props.initialElements.length === invalidElements.value.length;
        });
        const noElementsSelected = computed(() => {
            return props.initialElements.length === 0;
        });
        const tooFewElementsSelected = computed(() => {
            return workingElements.value.length < 2 && generatedPairs.value.length === 0;
        });
        // TODO: Include in the template
        // const showDuplicateError = computed(() => {
        //     return duplicatePairNames.value.length > 0;
        // });
        const noUnpairedElementsDisplayed = computed(() => {
            return numOfUnpairedForwardElements.value + numOfUnpairedReverseElements.value === 0;
        });
        // variables for datatype mapping and then filtering
        const datatypesMapperStore = useDatatypesMapperStore();
        const datatypesMapper = computed(() => datatypesMapperStore.datatypesMapper);
        /** Are we filtering by datatype? */
        const filterExtensions = computed(() => !!datatypesMapper.value && !!props.extensions?.length);
        function removeExtensionsToggle() {
            removeExtensions.value = !removeExtensions.value;
            generatedPairs.value.forEach((pair, index) => {
                pair.name = _guessNameForPair(pair.forward, pair.reverse, removeExtensions.value);
            });
        }
        watch(() => props.initialElements, () => {
            // initialize; and then for any new/removed elements, sync working elements
            _elementsSetUp();
        }, { immediate: true });
        /** Set up main data */
        function _elementsSetUp() {
            // a list of invalid elements and the reasons they aren't valid
            invalidElements.value = [];
            //TODO: handle fundamental problem of syncing DOM, views, and list here
            // data for list in progress
            workingElements.value = [];
            //TODO: this should maybe be in it's own method as it will get called everytime selected array has two elements and dumps again.
            // selectedDatasetElems = [];
            initialFiltersSet();
            // copy initial list, sort, // TODO: add ids if needed
            // copy initial list, sort, add ids if needed
            workingElements.value = JSON.parse(JSON.stringify(props.initialElements.slice(0)));
            // TODO: Here, we do what we do in other 2 creators; we check the already built pairs
            //       and sync them with the workingElements, ensuring that there aren't any elements
            //       in built pairs that no longer exist in workingElements
            // _ensureElementIds();
            _validateElements();
            _sortInitialList();
            // attempt to autopair
            if (props.fromSelection) {
                autoPair();
            }
        }
        function initialFiltersSet() {
            let illumina = 0;
            let dot12s = 0;
            let Rs = 0;
            //should we limit the forEach? What if there are 1000s of elements?
            props.initialElements.forEach((element) => {
                if (element.name?.includes(".1.fastq") || element.name?.includes(".2.fastq")) {
                    dot12s++;
                }
                else if (element.name?.includes("_R1") || element.name?.includes("_R2")) {
                    Rs++;
                }
                else if (element.name?.includes("_1") || element.name?.includes("_2")) {
                    illumina++;
                }
            });
            // if we cannot filter don't set an initial filter and hide all the data
            if (illumina == 0 && dot12s == 0 && Rs == 0) {
                forwardFilter.value = "";
                reverseFilter.value = "";
            }
            else if (illumina > dot12s && illumina > Rs) {
                changeFilters("illumina");
            }
            else if (dot12s > illumina && dot12s > Rs) {
                changeFilters("dot12s");
            }
            else if (Rs > illumina && Rs > dot12s) {
                changeFilters("Rs");
            }
            else {
                changeFilters("illumina");
            }
        }
        // TODO: Don't know if this is really necessary
        // function _ensureElementIds() {
        //     workingElements.value.forEach((element) => {
        //         if (!Object.prototype.hasOwnProperty.call(element, "id")) {
        //             console.warn("Element missing id", element);
        //         }
        //     });
        //     return workingElements.value;
        // }
        function _validateElements() {
            workingElements.value = workingElements.value.filter((element) => {
                var problem = _isElementInvalid(element);
                if (problem) {
                    invalidElements.value.push(element.name + "  " + problem);
                }
                return !problem;
            });
            return workingElements.value;
        }
        function _isElementInvalid(element) {
            if (element.history_content_type === "dataset_collection") {
                return localize("is a collection, this is not allowed");
            }
            var validState = element.state === STATES.OK || STATES.NOT_READY_STATES.includes(element.state);
            if (!validState) {
                return localize("has errored, is paused, or is not accessible");
            }
            if (element.deleted || element.purged) {
                return localize("has been deleted or purged");
            }
            // is the element's extension not a subtype of any of the required extensions?
            if (filterExtensions.value &&
                element.extension &&
                !datatypesMapper.value?.isSubTypeOfAny(element.extension, props.extensions)) {
                return localize(`has an invalid format: ${element.extension}`);
            }
            return null;
        }
        function _sortInitialList() {
            workingElements.value.sort((a, b) => naturalSort(a.name || "", b.name || ""));
        }
        function filterElements(elements, filterText) {
            return elements.filter((e) => filterElement(e, filterText));
        }
        function filterElement(element, filterText) {
            return filterText == "" || new RegExp(filterText).test(element.name || "");
        }
        function forwardElementSelected(e) {
            if (selectedForwardElement.value == null || selectedForwardElement.value.id != e.id) {
                selectedForwardElement.value = e;
            }
            else {
                selectedForwardElement.value = null;
            }
            if (selectedForwardElement.value &&
                selectedReverseElement.value &&
                selectedReverseElement.value != selectedForwardElement.value) {
                _pair(selectedForwardElement.value, selectedReverseElement.value);
            }
        }
        function reverseElementSelected(e) {
            if (selectedReverseElement.value == null || selectedReverseElement.value.id != e.id) {
                selectedReverseElement.value = e;
            }
            else {
                selectedReverseElement.value = null;
            }
            if (selectedForwardElement.value &&
                selectedReverseElement.value &&
                selectedReverseElement.value != selectedForwardElement.value) {
                _pair(selectedForwardElement.value, selectedReverseElement.value);
            }
        }
        // ------------------------------------------------------- pairing / unpairing
        /**
         * Create a pair from fwd and rev, removing them from unpaired,
         * and placing the new pair in paired
         */
        function _pair(fwd, rev, options = {}) {
            const pair = _createPair(fwd, rev, options.name);
            generatedPairs.value.push(pair);
            removePairFromUnpaired(fwd, rev);
            // if (!options.silent) {
            //     emit("pair:new", pair);
            // }
            return pair;
        }
        function removePairFromUnpaired(fwd, rev) {
            workingElements.value.splice(workingElements.value.indexOf(rev), 1);
            workingElements.value.splice(workingElements.value.indexOf(fwd), 1);
            selectedForwardElement.value = null;
            selectedReverseElement.value = null;
        }
        /**
         * Create a pair Object from fwd and rev, adding the name attribute
         * (will guess if not given)
         */
        function _createPair(fwd, rev, name) {
            // ensure existance and don't pair something with itself
            if (!(fwd && rev) || fwd === rev) {
                throw new Error(`Bad pairing: ${[JSON.stringify(fwd), JSON.stringify(rev)]}`);
            }
            name = name || _guessNameForPair(fwd, rev);
            return { forward: fwd, name: name, reverse: rev };
        }
        /** Try to find a good pair name for the given fwd and rev datasets */
        function _guessNameForPair(fwd, rev, willRemoveExtensions) {
            willRemoveExtensions = willRemoveExtensions ? willRemoveExtensions : removeExtensions.value;
            let fwdName = fwd.name;
            let revName = rev.name;
            const fwdNameFilter = fwdName?.replace(new RegExp(forwardFilter.value || ""), "");
            const revNameFilter = revName?.replace(new RegExp(reverseFilter.value || ""), "");
            if (!fwdNameFilter || !revNameFilter || !fwdName || !revName) {
                return `${fwdName} & ${revName}`;
            }
            let lcs = _naiveStartingAndEndingLCS(fwdNameFilter, revNameFilter);
            // remove url prefix if files were uploaded by url
            const lastSlashIndex = lcs.lastIndexOf("/");
            if (lastSlashIndex > 0) {
                const urlprefix = lcs.slice(0, lastSlashIndex + 1);
                lcs = lcs.replace(urlprefix, "");
            }
            if (willRemoveExtensions) {
                const lastDotIndex = lcs.lastIndexOf(".");
                if (lastDotIndex > 0) {
                    const extension = lcs.slice(lastDotIndex, lcs.length);
                    lcs = lcs.replace(extension, "");
                    fwdName = fwdName.replace(extension, "");
                    revName = revName.replace(extension, "");
                }
            }
            if (lcs.endsWith(".") || lcs.endsWith("_")) {
                lcs = lcs.substring(0, lcs.length - 1);
            }
            return lcs || `${fwdName} & ${revName}`;
        }
        function clickAutopair() {
            // Unselect any selected elements
            selectedForwardElement.value = null;
            selectedReverseElement.value = null;
            autoPair();
            workingElements.value = workingElements.value.filter((e) => {
                return !generatedPairs.value.some((pair) => pair.forward.id === e.id || pair.reverse.id === e.id);
            });
        }
        function clickUnpair(pair) {
            return _unpair(pair);
        }
        function clickClearFilters() {
            forwardFilter.value = "";
            reverseFilter.value = "";
        }
        function splitByFilter() {
            const filters = [new RegExp(forwardFilter.value), new RegExp(reverseFilter.value)];
            const split = [[], []];
            workingElements.value.forEach((e) => {
                filters.forEach((filter, i) => {
                    if (e.name && filter.test(e.name)) {
                        split[i]?.push(e);
                    }
                });
            });
            return split;
        }
        // ===========================================================================
        // -------------------------------------------------------------- auto pairing
        /** Autopair by exact match */
        function autoPairSimple(params) {
            return autoPairFnBuilder({
                match: (params) => {
                    params = params || {};
                    if (params.matchTo === params.possible) {
                        return {
                            index: params.index,
                            score: 1.0,
                        };
                    }
                    return params.bestMatch;
                },
                scoreThreshold: 0.6,
            })(params);
        }
        // TODO: Currently unused?
        /** Autopair by levenstein distance */
        // function autoPairLevenshtein(params: { listA: HDASummary[]; listB: HDASummary[] }) {
        //     return autoPairFnBuilder({
        //         match: (params) => {
        //             params = params || {};
        //             const distance = levenshteinDistance(params.matchTo, params.possible);
        //             const score = 1.0 - distance / Math.max(params.matchTo.length, params.possible.length);
        //             if (score > params.bestMatch.score) {
        //                 return {
        //                     index: params.index,
        //                     score: score,
        //                 };
        //             }
        //             return params.bestMatch;
        //         },
        //         scoreThreshold: MATCH_PERCENTAGE,
        //     })(params);
        // }
        /** Autopair by longest common substrings scoring */
        function autoPairLCS(params) {
            return autoPairFnBuilder({
                match: (params) => {
                    params = params || {};
                    const match = _naiveStartingAndEndingLCS(params.matchTo, params.possible).length;
                    const score = match / Math.max(params.matchTo.length, params.possible.length);
                    if (score > params.bestMatch.score) {
                        return {
                            index: params.index,
                            score: score,
                        };
                    }
                    return params.bestMatch;
                },
                scoreThreshold: MATCH_PERCENTAGE,
            })(params);
        }
        /**
         * Two passes to automatically create pairs:
         * use both simpleAutoPair, then the fn mentioned in strategy
         */
        function autoPair(localStrategy) {
            let split = splitByFilter();
            let paired = [];
            if (twoPassAutoPairing.value) {
                const simplePaired = autoPairSimple({
                    listA: split[0],
                    listB: split[1],
                });
                paired = simplePaired ? simplePaired : paired;
                split = splitByFilter();
            }
            // then try the remainder with something less strict
            localStrategy = localStrategy || strategy.value;
            split = splitByFilter();
            const pairedStrategy = localStrategy({
                listA: split[0],
                listB: split[1],
            });
            paired = pairedStrategy ? paired.concat(pairedStrategy) : paired;
            autoPairsPossible.value = paired.length > 0;
            firstAutoPairDone.value = true;
            return paired;
        }
        /** Add a dataset to the unpaired list in it's proper order */
        function _addToUnpaired(dataset) {
            /** Currently, unpaired is natural sorted by name, use binary search to find insertion point */
            function binSearchSortedIndex(low, hi) {
                if (low === hi) {
                    return low;
                }
                let mid = Math.floor((hi - low) / 2) + low;
                const elementAtPoint = workingElements.value[mid];
                const compared = naturalSort(dataset.name || "", elementAtPoint?.name || "");
                if (compared < 0) {
                    return binSearchSortedIndex(low, mid);
                }
                else if (compared > 0) {
                    return binSearchSortedIndex(mid + 1, hi);
                }
                else if (!elementAtPoint) {
                    return mid;
                }
                // walk the equal to find the last // TODO: Investigate this, is this correct?
                if (elementAtPoint?.name === dataset.name) {
                    mid++;
                }
                return mid;
            }
            workingElements.value.splice(binSearchSortedIndex(0, workingElements.value.length), 0, dataset);
        }
        /**
         * Unpair a pair, removing it from paired, and adding the fwd,rev
         * datasets back into unpaired
         */
        function _unpair(pair) {
            if (!pair) {
                throw new Error(`Bad pair: ${JSON.stringify(pair)}`);
            }
            generatedPairs.value.splice(generatedPairs.value.indexOf(pair), 1);
            _addToUnpaired(pair.forward);
            _addToUnpaired(pair.reverse);
            return pair;
        }
        /** Unpair all paired datasets */
        function unpairAll() {
            const pairs = [];
            while (generatedPairs.value.length) {
                if (generatedPairs.value[0]) {
                    pairs.push(_unpair(generatedPairs.value[0]));
                }
                // pairs.push(_unpair(this.generatedPairs[0], { silent: true }));
            }
        }
        // ===========================================================================
        /** Returns an autopair function that uses the provided options.match function */
        function autoPairFnBuilder(options) {
            options = options || {};
            options.createPair =
                options.createPair ||
                    function _defaultCreatePair(params) {
                        params = params || {};
                        const a = params.listA.splice(params.indexA, 1)[0];
                        const b = params.listB.splice(params.indexB, 1)[0];
                        if (!a || !b) {
                            return undefined;
                        }
                        const aInBIndex = params.listB.indexOf(a);
                        const bInAIndex = params.listA.indexOf(b);
                        if (aInBIndex !== -1) {
                            params.listB.splice(aInBIndex, 1);
                        }
                        if (bInAIndex !== -1) {
                            params.listA.splice(bInAIndex, 1);
                        }
                        // return _pair(a, b, { silent: true });
                        return _pair(a, b);
                    };
            // compile these here outside of the loop
            let _regexps = [];
            function getRegExps() {
                if (!_regexps.length) {
                    _regexps = [new RegExp(forwardFilter.value), new RegExp(reverseFilter.value)];
                }
                return _regexps;
            }
            // mangle params as needed
            options.preprocessMatch =
                options.preprocessMatch ||
                    function _defaultPreprocessMatch(params) {
                        const regexps = getRegExps();
                        return Object.assign(params, {
                            matchTo: params.matchTo.name?.replace(regexps[0] || "", ""),
                            possible: params.possible.name?.replace(regexps[1] || "", ""),
                            index: params.index,
                            bestMatch: params.bestMatch,
                        });
                    };
            return function _strategy(params) {
                params = params || {};
                const listA = params.listA;
                const listB = params.listB;
                let indexA = 0;
                let indexB;
                let bestMatch = {
                    score: 0.0,
                    index: -1,
                };
                const paired = [];
                while (indexA < listA.length) {
                    const matchTo = listA[indexA];
                    bestMatch.score = 0.0;
                    if (!matchTo) {
                        continue;
                    }
                    for (indexB = 0; indexB < listB.length; indexB++) {
                        const possible = listB[indexB];
                        if (listA[indexA] !== listB[indexB]) {
                            bestMatch = options.match(options.preprocessMatch({
                                matchTo: matchTo,
                                possible: possible,
                                index: indexB,
                                bestMatch: bestMatch,
                            }));
                            if (bestMatch.score === 1.0) {
                                break;
                            }
                        }
                    }
                    const scoreThreshold = options.scoreThreshold ? options.scoreThreshold : MATCH_PERCENTAGE;
                    if (bestMatch.score >= scoreThreshold) {
                        const createdPair = options.createPair({
                            listA: listA,
                            indexA: indexA,
                            listB: listB,
                            indexB: bestMatch.index,
                        });
                        if (createdPair) {
                            paired.push(createdPair);
                        }
                    }
                    else {
                        indexA += 1;
                    }
                    if (!listA.length || !listB.length) {
                        return paired;
                    }
                }
                return paired;
            };
        }
        function changeFilters(filter) {
            forwardFilter.value = COMMON_FILTERS[filter][0];
            reverseFilter.value = COMMON_FILTERS[filter][1];
        }
        function addUploadedFiles(files) {
            // Any uploaded files are added to workingElements in _elementsSetUp
            // The user will have to manually select the files to add them to the pair
            // Check for validity of uploads
            files.forEach((file) => {
                const problem = _isElementInvalid(file);
                if (problem) {
                    const invalidMsg = `${file.hid}: ${file.name} ${problem} and ${NOT_VALID_ELEMENT_MSG}`;
                    invalidElements.value.push(invalidMsg);
                    Toast.error(invalidMsg, localize("Uploaded item invalid for pair"));
                }
            });
        }
        const { confirm } = useConfirmDialog();
        async function clickedCreate(collectionName) {
            checkForDuplicates();
            atLeastOnePair.value = generatedPairs.value.length > 0;
            let confirmed = false;
            if (!atLeastOnePair.value) {
                confirmed = await confirm("Are you sure you want to create a list with no pairs?", {
                    title: "Create an empty list of pairs",
                    okTitle: "Create",
                    okVariant: "primary",
                });
            }
            if (state.value == "build" && (atLeastOnePair.value || confirmed)) {
                emit("clicked-create", generatedPairs.value, collectionName, hideSourceItems.value);
            }
        }
        function checkForDuplicates() {
            const existingPairNames = {};
            duplicatePairNames.value = [];
            let valid = true;
            generatedPairs.value.forEach((pair) => {
                if (Object.prototype.hasOwnProperty.call(existingPairNames, pair.name)) {
                    valid = false;
                    duplicatePairNames.value.push(pair.name);
                }
                existingPairNames[pair.name] = true;
            });
            state.value = valid ? "build" : "duplicates";
        }
        // TODO: Where is this being used?
        // function stripExtension(name: string) {
        //     return name.includes(".") ? name.substring(0, name.lastIndexOf(".")) : name;
        // }
        /** Return the concat'd longest common prefix and suffix from two strings */
        function _naiveStartingAndEndingLCS(s1, s2) {
            var fwdLCS = "";
            var revLCS = "";
            var i = 0;
            var j = 0;
            while (i < s1.length && i < s2.length) {
                if (s1[i] !== s2[i]) {
                    break;
                }
                fwdLCS += s1[i];
                i += 1;
            }
            if (i === s1.length) {
                return s1;
            }
            if (i === s2.length) {
                return s2;
            }
            i = s1.length - 1;
            j = s2.length - 1;
            while (i >= 0 && j >= 0) {
                if (s1[i] !== s2[j]) {
                    break;
                }
                revLCS = [s1[i], revLCS].join("");
                i -= 1;
                j -= 1;
            }
            return fwdLCS + revLCS;
        }
        return { __sfc: true, NOT_VALID_ELEMENT_MSG, COMMON_FILTERS, DEFAULT_FILTER, MATCH_PERCENTAGE, CHOOSE_FILTER_TITLE, FILTER_TEXT_PLACEHOLDER, FILTER_TEXT_TITLE, ERROR_TEXT, INVALID_HEADER, ALL_INVALID_ELEMENTS_PART_ONE, CANCEL_TEXT, props, emit, noElementsHeader, allInvalidElementsPartTwo, state, hideSourceItems, autoPairsPossible, twoPassAutoPairing, removeExtensions, firstAutoPairDone, showPairingSection, workingElements, invalidElements, generatedPairs, selectedForwardElement, selectedReverseElement, atLeastOnePair, forwardFilter, reverseFilter, hasFilter, strategy, duplicatePairNames, canClearFilters, canAutoPair, forwardElements, reverseElements, pairableElements, autoPairButton, numOfUnpairedForwardElements, numOfFilteredOutForwardElements, numOfUnpairedReverseElements, numOfFilteredOutReverseElements, numOfPairs, returnInvalidElementsLength, returnInvalidElements, allElementsAreInvalid, noElementsSelected, tooFewElementsSelected, noUnpairedElementsDisplayed, datatypesMapperStore, datatypesMapper, filterExtensions, removeExtensionsToggle, _elementsSetUp, initialFiltersSet, _validateElements, _isElementInvalid, _sortInitialList, filterElements, filterElement, forwardElementSelected, reverseElementSelected, _pair, removePairFromUnpaired, _createPair, _guessNameForPair, clickAutopair, clickUnpair, clickClearFilters, splitByFilter, autoPairSimple, autoPairLCS, autoPair, _addToUnpaired, _unpair, unpairAll, autoPairFnBuilder, changeFilters, addUploadedFiles, confirm, clickedCreate, checkForDuplicates, _naiveStartingAndEndingLCS, faAngleDown, faAngleUp, faTimes, faUnlink, FontAwesomeIcon, BAlert, BButton, BButtonGroup, BCard, BCardBody, BCardHeader, draggable, localize, Heading, PairedElementView, UnpairedDatasetElementView, CollectionCreator };
    }
});
